import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAssets as useAssetsHook } from "../../assets/api/assets";
import Button from "../../common/components/Button";
import { toast } from "../../common/components/Toast";
import { updateProjectMember } from "../api/members";

function EditMemberModal({
  project,
  setProject,
  showEditMemberModal,
  setShowEditMemberModal,
  selectedMember,
}) {
  const { t } = useTranslation("members");
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const { assets } = useAssetsHook(1, 1000);

  useEffect(() => {
    if (!selectedMember) return;

    if (showEditMemberModal && selectedMember) {
      setName(selectedMember.user_details?.name || "");
      setPhone(selectedMember.user_details?.phone || "");
      setSelectedRole(selectedMember.role || "producer");
      setSelectedAssets(
        (selectedMember.accessible_assets || []).map((a) =>
          typeof a === "object" ? a.asset_id : a,
        ),
      );
      setShowAssetSelection(selectedMember.role === "producer");
    }
  }, [showEditMemberModal, selectedMember]);

  const handleCloseModal = () => {
    setShowEditMemberModal(false);
    setSelectedRole("");
    setSelectedAssets([]);
    setShowAssetSelection(false);
    setName("");
    setPhone("");
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);

    if (role === "producer") {
      setShowAssetSelection(true);
    } else {
      setShowAssetSelection(false);
      setSelectedAssets([]);
    }
  };

  const handleSave = async () => {
    if (!selectedMember) return;

    if (selectedRole === "producer" && selectedAssets.length === 0) {
      toast.error(t("editModal.assetsRequired"));
      return;
    }

    const data = {
      role: selectedRole,
      asset_ids: selectedRole === "producer" ? selectedAssets : [],
      name: name.trim() || undefined,
      phone: phone.trim(),
    };

    setLoading(true);
    try {
      const updatedProjectUser = await updateProjectMember(
        selectedMember.id,
        data,
      );

      setProject({
        ...project,
        users: project.users.map((user) =>
          user.id === selectedMember.id ? updatedProjectUser : user,
        ),
      });

      handleCloseModal();
      toast.success(t("messages.updatedSuccess"));
    } catch (error) {
      console.error("Error updating member:", error);
      toast.error(t("messages.updatedError"));
    } finally {
      setLoading(false);
    }
  };

  if (!selectedMember) return null;

  return (
    <Dialog
      open={showEditMemberModal}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 3 }}>{t("editModal.title")}</DialogTitle>

      <DialogContent>
        {/* Member Avatar */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Avatar
            src={selectedMember?.user_details?.avatar}
            sx={{ width: 50, height: 50 }}
          />
          <Box>
            <Typography variant="bodySm" color="text.secondary">
              {selectedMember?.user_details?.email}
            </Typography>
          </Box>
        </Box>

        {/* Profile Fields */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            label={t("editModal.nameLabel")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label={t("editModal.phoneLabel")}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
        </Box>

        {/* Role and Asset Selection */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>{t("editModal.roleLabel")}</InputLabel>
            <Select
              value={selectedRole}
              label={t("editModal.roleLabel")}
              onChange={(e) => handleRoleChange(e.target.value)}
            >
              <MenuItem value="producer">{t("roles.producer")}</MenuItem>
              <MenuItem value="owner">{t("roles.owner")}</MenuItem>
            </Select>
          </FormControl>

          {showAssetSelection && (
            <FormControl sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel>{t("editModal.assetsLabel")}</InputLabel>
              <Select
                multiple
                value={selectedAssets}
                onChange={(e) => setSelectedAssets(e.target.value)}
                input={<OutlinedInput label={t("editModal.assetsLabel")} />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((assetId) => {
                      const asset = assets.find((p) => p.id === assetId);
                      return (
                        <Chip
                          key={assetId}
                          label={asset?.title || `Asset ${assetId}`}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {assets.length > 0 ? (
                  assets.map((asset) => (
                    <MenuItem key={asset.id} value={asset.id}>
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedAssets.includes(asset.id)}
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText primary={asset.title} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <ListItemText primary={t("editModal.noAssetsAvailable")} />
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="secondary" onClick={handleCloseModal}>
          {t("editModal.cancel")}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {t("editModal.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditMemberModal;
